import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import { useTheme } from '../context/ThemeContext';

const navItems = [
    { name: 'Services', path: '/services' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
];

export default function Navbar() {
    const location = useLocation();
    const settings = useSettings();
    const { theme, toggleTheme } = useTheme();
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const isActive = (path) => {
        const current = location.pathname.replace(/\/$/, '') || '/';
        const target = path.replace(/\/$/, '') || '/';
        
        // Direct match
        if (current === target) return true;
        
        // Highlight "Services" if on a service detail page
        if (target === '/services' && current.startsWith('/service/')) return true;
        
        // Highlight "Portfolio" if on a portfolio detail page
        if (target === '/portfolio' && current.startsWith('/portfolio/')) return true;
        
        return false;
    };

    return (
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-nocturne-bg/90 backdrop-blur-md border-b border-nocturne-border/60 shadow-lg shadow-black/20' : 'bg-transparent'}`}>
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                {/* Logo */}
                <Link to="/" className="text-xl font-bold tracking-wide text-nocturne-text hover:text-nocturne-purple transition-colors">
                    {settings.logo ? (
                        <img src={settings.logo} alt="Logo" className="h-8 w-auto object-contain" />
                    ) : (
                        'AMISUBSOLUTIONS'
                    )}
                </Link>

                {/* Desktop nav */}
                <div className="hidden md:flex items-center gap-8">
                    {navItems.map(item => (
                        <Link
                            key={item.name}
                            to={item.path}
                            className={`nav-link relative pb-1 ${isActive(item.path) ? 'active text-nocturne-text' : ''}`}
                        >
                            {item.name}
                            {isActive(item.path) && (
                                <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-nocturne-purple rounded-full" />
                            )}
                        </Link>
                    ))}
                </div>

                {/* Right side */}
                <div className="hidden md:flex items-center gap-4">
                    <button 
                        onClick={toggleTheme}
                        className="w-10 h-10 rounded-full bg-nocturne-card border border-nocturne-border flex items-center justify-center text-xl hover:border-nocturne-purple/40 transition-all active:scale-90"
                        title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
                    >
                        {theme === 'dark' ? '☀️' : '🌙'}
                    </button>
                    <Link
                        to="/contact"
                        className="btn-primary text-xs px-5 py-2.5"
                    >
                        Get Started
                    </Link>
                </div>

                {/* Mobile hamburger */}
                <button
                    className="md:hidden text-nocturne-text hover:text-nocturne-purple transition-colors"
                    onClick={() => setMenuOpen(!menuOpen)}
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {menuOpen
                            ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        }
                    </svg>
                </button>
            </div>

            {/* Mobile menu */}
            {menuOpen && (
                <div className="md:hidden bg-nocturne-bg/95 backdrop-blur-lg border-t border-nocturne-border px-6 py-8 flex flex-col gap-6 shadow-2xl animate-fade-in">
                    {navItems.map(item => (
                        <Link
                            key={item.name}
                            to={item.path}
                            onClick={() => setMenuOpen(false)}
                            className={`text-left text-lg font-bold tracking-tight py-1 transition-all ${isActive(item.path) ? 'text-nocturne-purple' : 'text-nocturne-text hover:text-nocturne-purple'}`}
                        >
                            {item.name}
                        </Link>
                    ))}
                    <div className="flex items-center justify-between mt-2 pt-4 border-t border-nocturne-border">
                        <span className="text-xs font-semibold text-nocturne-muted uppercase tracking-wider">Appearance</span>
                        <button 
                            onClick={toggleTheme}
                            className="w-10 h-10 rounded-full bg-nocturne-bg border border-nocturne-border flex items-center justify-center text-xl"
                        >
                            {theme === 'dark' ? '☀️' : '🌙'}
                        </button>
                    </div>
                    <Link to="/contact" onClick={() => setMenuOpen(false)} className="btn-primary w-full mt-2 px-5 py-2.5 text-center justify-center">
                        Get Started
                    </Link>
                </div>
            )}
        </nav>
    );
}
